import ButtonWithIcon from '@/components/common/ButtonWithIcon';
import { useDrawer } from '@/components/common/DrawerManager';
import { FlexCol, FlexRow } from '@/components/common/Flex';
import { BarChart01, PlusCircle } from '@/components/common/icons';
import { Text } from '@/components/common/Typography';
import AccountLockup from '@/components/financialEntities/AccountLockup';
import { startFinancialEntityAddFlow } from '@/components/financialEntities/FinancialEntityAddFlow';
import FinancialEntityBalance from '@/components/financialEntities/FinancialEntityBalance';
import { Spacer } from '@chakra-ui/react';
import { useOnCompleteHandlers } from './helpers';
import { showIncludedAccountsModal } from './IncludedAccountsModal';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

const RetirementAccountList = () => {
    const {
        possessiveStr,
        includedAccounts,
        goalAccounts,
        partnerSwitcherType,
        goalConfig,
    } = useRetirementGoalContext();

    const { showDrawer } = useDrawer();

    const isEmptyState = goalAccounts.length + includedAccounts.length === 0;

    return (
        <FlexCol
            minH={160}
            px={4}
            py={4}
            bg="gray.50"
            rounded="md"
            data-cy="retirement-account-list"
        >
            <FlexRow justifyContent="space-between" alignItems="center">
                <Text fontWeight={600} fontSize="md" pl={2} py={2}>
                    {`${possessiveStr} retirement accounts`}
                </Text>
                {partnerSwitcherType === 'MINE' && <AddAccountButton />}
            </FlexRow>
            {isEmptyState ? (
                <FlexRow
                    justifyContent="center"
                    alignItems="center"
                    h="full"
                    color="gray.400"
                >
                    <BarChart01 />
                    <Text fontSize="sm" pl={1} pt={1}>
                        Track 401ks, IRAs and retirement savings
                    </Text>
                </FlexRow>
            ) : (
                <FlexCol mt={4}>
                    {[...goalAccounts, ...includedAccounts].map((account) => {
                        // Detail view can be opened for all external FEs
                        // and approved PlentyGoalAccounts
                        const onClick =
                            account.type !== 'PlentyGoalAccount'
                                ? () =>
                                      showDrawer({
                                          drawerKey: 'financial-entity-detail',
                                          entityId: account.id,
                                      })
                                : account.goalAccountOnboarding?.approvedAt
                                  ? () =>
                                        showDrawer({
                                            drawerKey: 'goal-account-detail',
                                            entityId: account.id,
                                        })
                                  : undefined;
                        return (
                            <FlexRow
                                key={account.id}
                                py={4}
                                borderTopWidth={1}
                                borderColor="border"
                                cursor={onClick ? 'pointer' : undefined}
                                onClick={onClick}
                            >
                                <AccountLockup
                                    account={account}
                                    iconProps={
                                        // Use goal color for goal accounts
                                        account.type === 'PlentyGoalAccount'
                                            ? { color: goalConfig.color }
                                            : undefined
                                    }
                                />
                                <Spacer />
                                <FinancialEntityBalance fe={account} />
                            </FlexRow>
                        );
                    })}
                </FlexCol>
            )}
        </FlexCol>
    );
};

const AddAccountButton = () => {
    const { goal, isSharedGoal, myIncludableAccounts, includedAccounts } =
        useRetirementGoalContext();
    const { onCompleteConnection, onCompleteManual } = useOnCompleteHandlers();
    const label = includedAccounts.length === 0 ? 'Add' : 'Edit';

    return (
        <ButtonWithIcon
            variant="pill"
            colorScheme="white"
            iconLeft={<PlusCircle color="text" />}
            h={9}
            size="sm"
            onClick={() => {
                if (myIncludableAccounts.length) {
                    // If includable accounts exist, show "Which accounts" modal
                    showIncludedAccountsModal({
                        goalId: goal.id,
                        isSharedGoal,
                    });
                } else {
                    // Otherwise, start the "Add account" modal flow
                    startFinancialEntityAddFlow({
                        isInRetirementContext: true,
                        onCompleteConnection,
                        onCompleteManual,
                    });
                }
            }}
            onClickEvent="Add Account Button Clicked"
            onClickEventParams={{
                label,
                location: 'Retirement goal accounts list',
            }}
        >
            {label}
        </ButtonWithIcon>
    );
};

export default RetirementAccountList;
